import { Route, Router, Switch, useLocation } from 'wouter'
import { BasedClient } from '@based/sdk/client'
import { Provider as BasedClientProvider } from '@based/sdk/react'
import { NuqsAdapter } from 'nuqs/adapters/react'
import { Landing } from './pages/landing/Landing'
import { ProfilePage } from './pages/Profile'
import { PlotPage } from './pages/discover/plots/PlotPage'
import { Discover } from './pages/discover/Discover'
import { DiscoverServiceProviders } from './pages/discover/service-providers/DiscoverServiceProviders'
import { ServiceProviderPage } from './pages/discover/service-providers/ServiceProviderPage'
import { ServiceProviderConceptPage } from './pages/discover/service-providers/service-provider-concept/ServiceProviderConceptPage'
import { BlogItemPage } from './pages/blog/BlogItem'
import { BlogsPage } from './pages/blog'
import { AboutUs } from './pages/info/AboutUs'
import { KnowledgeHub } from './pages/knowledge-hub'
import { KnowledgeHubModulePage } from './pages/knowledge-hub/KnowledgeHubModule'
import { pages, redirects, externalPages } from './util/links'
import { ContactUs } from './pages/info/ContactUs'
import { Legal } from './pages/info/Legal'
import { ForExperts } from './pages/info/ForExperts'
import { HousingFormPage } from './pages/discover/housing-forms/HousingFormPage'
import { TopicCommunityPage } from './pages/discover/topic-communities/TopicCommunityPage'
import { ProfileHome } from './pages/dashboard/profile/ProfileHome'
import { ProfileMessages } from './pages/dashboard/profile/ProfileMessages'
import { ProfileView } from './pages/dashboard/profile/ProfileView'
import { ProfilePreferences } from './pages/dashboard/profile/ProfilePreferences'
import { StewardOwnership } from './pages/info/StewardOwnership'
import { ProfileFollowing } from './pages/dashboard/profile/ProfileFollowing'
import { InitiativeHome } from './pages/dashboard/initiative/InitiativeHome'
import { InitiativeCreation } from './pages/dashboard/initiative/create/InitiativeCreation'
import { AuthPage } from './pages/auth/AuthPage'
import { RegionBusinessProfilePage } from './pages/discover/regions/RegionBusinessProfilepage'
import { RegionPage } from './pages/discover/regions/RegionPage'
import { InitiativeAbout } from './pages/dashboard/initiative/view/InitiativeAbout'
import { InitiativePeople } from './pages/dashboard/initiative/view/InitiativePeople'
import { InitiativePage } from './pages/discover/initiatives/InitiativePage'
import { NotFoundPage } from './pages/error/NotFoundPage'
import { AuthModalProvider } from './util/contexts/AuthModalProvider'
import { SsrContextProvider } from './util/contexts/SsrContextProvider'
import { MessageBoxProvider } from './util/contexts/MessageBoxProvider'
import { ToastProvider } from './util/contexts/ToastProvider'
import { UserProvider } from './util/contexts/UserContextProvider'
import { ElearningItemPage } from './pages/elearning/ElearningItem'
import { Elearning } from './pages/elearning'
import { ForMunicipalities } from './pages/info/ForMunicipalities'
import { ErrorBoundary } from './pages/error/ErrorBoundary'
import { MunicipalityPage } from './pages/discover/regions/MunicipalityPage'
import { useEffect, useRef } from 'react'
import { RabobankLanding } from './pages/info/RabobankLanding'
import { PlotRegistryFlow } from './pages/processes/plot-registry/PlotRegistryFlow'
import { SsrContextType } from './util/contexts/SsrContext'

function RedirectRoute({
  to,
  external,
}: {
  to: string
  external?: boolean
}): null {
  const [, navigate] = useLocation()

  useEffect(() => {
    if (external) {
      if (typeof window !== 'undefined') {
        window.location.replace(to)
      }
    } else {
      navigate(to, { replace: true })
    }
  }, [to, external, navigate])

  return null
}

function ScrollToTop(): null {
  const [location] = useLocation()
  const oldLocationRef = useRef(location || '')

  useEffect(() => {
    if (oldLocationRef.current !== location) {
      const prefersReducedMotion =
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      })
    }

    oldLocationRef.current = location
  }, [location])

  return null
}

const App = ({
  ssrPath,
  basedClient,
  ssrContext = {},
}: {
  ssrPath?: string
  basedClient?: BasedClient
  ssrContext?: SsrContextType
}) => {
  return (
    <Router ssrPath={ssrPath}>
      <BasedClientProvider client={basedClient}>
        <SsrContextProvider {...ssrContext}>
          <NuqsAdapter>
            <ErrorBoundary>
              <AuthModalProvider>
                <ToastProvider>
                  <UserProvider>
                    <MessageBoxProvider>
                      <ScrollToTop />
                      <Switch>
                        <Route path={pages.home} component={Landing} />
                        <Route path={pages.login}>
                          {() => <AuthPage tab="login" />}
                        </Route>
                        <Route path={pages.signup}>
                          {() => <AuthPage tab="signUp" />}
                        </Route>
                        <Route path={pages.profile(':userId')}>
                          {(params: { userId: string }) => (
                            <ProfilePage userId={params.userId} />
                          )}
                        </Route>
                        {/* Discover */}
                        <Route path={pages.discover}>
                          {() => <Discover tab="initiatives" />}
                        </Route>
                        <Route path={pages.initiatives}>
                          {() => <Discover tab="initiatives" />}
                        </Route>
                        <Route
                          path={pages.initiativesWithApplyPhase(':phaseType')}
                        >
                          {() => <Discover tab="initiatives" />}
                        </Route>
                        <Route path={pages.plots}>
                          {() => <Discover tab="locations" />}
                        </Route>
                        <Route path={pages.regions}>
                          {() => <Discover tab="regions" />}
                        </Route>
                        <Route path={pages.housingForms}>
                          {() => <Discover tab="housingForms" />}
                        </Route>
                        <Route path={pages.plot(':plotId')}>
                          {(params: { plotId: string }) => (
                            <PlotPage plotId={params.plotId} />
                          )}
                        </Route>
                        <Route path={pages.initiative(':key')}>
                          {(params: { key: string }) => (
                            <InitiativePage initiativeId={params.key} />
                          )}
                        </Route>
                        <Route path={pages.housingForm(':key')}>
                          {(params: { key: string }) => (
                            <HousingFormPage id={params.key} />
                          )}
                        </Route>
                        <Route path={pages.topicCommunity(':key')}>
                          {(params: { key: string }) => (
                            <TopicCommunityPage id={params.key} />
                          )}
                        </Route>

                        {/* Regions */}
                        <Route path={pages.regionPage(':slug')}>
                          {(params: { slug: string }) => (
                            <RegionPage slug={params.slug} />
                          )}
                        </Route>
                        <Route path={pages.municipalityPage(':municipalityId')}>
                          {(params: { municipalityId: string }) => (
                            <MunicipalityPage slug={params.municipalityId} />
                          )}
                        </Route>
                        <Route
                          path={pages.regionalBusinessProfile(':profileId')}
                        >
                          {(params: { profileId: string }) => (
                            <RegionBusinessProfilePage
                              businessProfileId={params.profileId}
                            />
                          )}
                        </Route>

                        {/* Service Providers */}
                        <Route
                          path={pages.serviceProviders}
                          component={DiscoverServiceProviders}
                        />
                        <Route
                          path={pages.serviceProvider(':serviceProviderSlug')}
                        >
                          {(params: { serviceProviderSlug: string }) => (
                            <ServiceProviderPage
                              serviceProviderSlug={params.serviceProviderSlug}
                            />
                          )}
                        </Route>
                        <Route path={pages.serviceProviderConcept(':slug')}>
                          {(params: { slug: string }) => (
                            <ServiceProviderConceptPage slug={params.slug} />
                          )}
                        </Route>

                        {/* Knowledge Hub & E-learning */}
                        <Route
                          path={pages.knowledgeHub}
                          component={KnowledgeHub}
                        />
                        <Route path={pages.knowledgeHubModule(':key')}>
                          {(params: { key: string }) => (
                            <KnowledgeHubModulePage itemId={params.key} />
                          )}
                        </Route>
                        <Route path={pages.elearning} component={Elearning} />
                        <Route path={pages.elearningItem(':key')}>
                          {(params: { key: string }) => (
                            <ElearningItemPage itemSlug={params.key} />
                          )}
                        </Route>

                        {/* Blog */}
                        <Route path={pages.blog(':blogType', ':blogId')}>
                          {(params: { blogId: string }) => (
                            <BlogItemPage blogId={params.blogId} />
                          )}
                        </Route>
                        <Route path={pages.blogs()} component={BlogsPage} />
                        <Route
                          path={pages.blogs(':blogType')}
                          component={BlogsPage}
                        />

                        {/* Info Pages */}
                        <Route path={pages.aboutUs} component={AboutUs} />
                        <Route
                          path={pages.forMunicipalities}
                          component={ForMunicipalities}
                        />
                        <Route
                          path={pages.stewardOwnership}
                          component={StewardOwnership}
                        />
                        <Route path={pages.contactUs} component={ContactUs} />
                        <Route path={pages.forExperts} component={ForExperts} />
                        <Route path={pages.legal} component={Legal} />
                        <Route
                          path={pages.rabobankLanding}
                          component={RabobankLanding}
                        />

                        {/* Dashboard */}
                        <Route path={pages.dashboard} component={ProfileHome} />
                        <Route path={pages.dashboardMessages}>
                          {() => <ProfileMessages />}
                        </Route>
                        <Route path={pages.dashboardViewConversation(':id')}>
                          {(params: { id: string }) => (
                            <ProfileMessages
                              conversationId={Number(params.id)}
                            />
                          )}
                        </Route>
                        <Route
                          path={pages.dashboardInitiatives}
                          component={InitiativeHome}
                        />
                        <Route path={pages.dashboardViewInitiative(':id')}>
                          {(params: { id: string }) => (
                            <InitiativeAbout initiativeId={params.id} />
                          )}
                        </Route>
                        <Route
                          path={pages.dashboardManagePeopleInitiative(':id')}
                        >
                          {(params: { id: string }) => (
                            <InitiativePeople initiativeId={params.id} />
                          )}
                        </Route>
                        <Route path={pages.dashboardStartInitiative}>
                          <InitiativeCreation />
                        </Route>
                        <Route
                          path={pages.dashboardOnboardInitiative(
                            ':id',
                            ':step',
                          )}
                        >
                          {(params: { id: string; step?: string }) => (
                            <InitiativeCreation
                              initiativeId={params.id}
                              currentStep={params.step}
                            />
                          )}
                        </Route>
                        <Route
                          path={pages.dashboardProfile}
                          component={ProfileView}
                        />
                        <Route
                          path={pages.dashboardPreferences}
                          component={ProfilePreferences}
                        />
                        <Route
                          path={pages.dashboardFollowing}
                          component={ProfileFollowing}
                        />

                        <Route
                          path={pages.plotRegistry}
                          component={PlotRegistryFlow}
                        />

                        {/* Events (external redirect) */}
                        <Route path={pages.events}>
                          {() => (
                            <RedirectRoute to={externalPages.events} external />
                          )}
                        </Route>

                        {/* Legacy redirects */}
                        {redirects.map(({ from, to, external }) => (
                          <Route key={from} path={from}>
                            {(params) => (
                              <RedirectRoute
                                to={
                                  typeof to === 'function'
                                    ? to(params as Record<string, string>)
                                    : to
                                }
                                external={external}
                              />
                            )}
                          </Route>
                        ))}

                        <Route component={NotFoundPage} />
                      </Switch>
                    </MessageBoxProvider>
                  </UserProvider>
                </ToastProvider>
              </AuthModalProvider>
            </ErrorBoundary>
          </NuqsAdapter>
        </SsrContextProvider>
      </BasedClientProvider>
    </Router>
  )
}

export default App
